// File: packages/api-client/src/radar.ts
// Version: 2026.4.24 | ForgeRadar API client — leads, sources, review, dashboard
//
// Mirrors the Phase 1 Console API surface defined in forgeradar-architecture.md.
// All endpoints are tenant-scoped via the standard ForgeRadar tenant header
// (X-Tenant-Id) injected by the API server's TenantResolutionMiddleware on
// authenticated calls.

import { useCallback, useMemo } from 'react';
import { useApiClient } from './client';

// ════════════════════════════════════════════════════════════════════════════
// Domain types
// ════════════════════════════════════════════════════════════════════════════

export type LeadTier = 'hot' | 'warm' | 'cold';

export type LeadCategory =
  | 'r_d_tax_credit'
  | 'ai_dev_measurement'
  | 'investor_dd'
  | 'gov_rfp'
  | 'accelerator_vc'
  | 'compliance_audit';

export type LeadStatus =
  | 'new'
  | 'in_review'
  | 'drafted'
  | 'sent'
  | 'won'
  | 'lost'
  | 'archived';

export type SourceFamily = 'gov' | 'founder' | 'professional' | 'social';
export type LegalRisk = 'Low' | 'Medium' | 'High';

export interface RadarLeadSummary {
  leadId: string;
  artifactId: string;
  title: string;
  sourceCode: string;
  category: LeadCategory;
  score: number;            // 0–100
  tier: LeadTier;
  status: LeadStatus;
  capturedAt: string;       // ISO 8601
  contactName?: string | null;
  companyName?: string | null;
  estimatedValue?: number | null;
  responseDeadline?: string | null;
  requiresHumanReview: boolean;
  confidence: number;       // 0–1
}

export interface RadarLeadDetail extends RadarLeadSummary {
  url: string;
  publishedAt?: string | null;
  normalizedText: string;
  rationaleByCategory: Partial<Record<LeadCategory, { score: number; rationale: string }>>;
  extractedEntities: {
    contactName?: string | null;
    contactEmail?: string | null;
    contactPhone?: string | null;
    companyName?: string | null;
    companyDomain?: string | null;
    estimatedValue?: number | null;
    responseDeadline?: string | null;
  };
  notes?: string | null;
  assignedTo?: string | null;
  responses: RadarLeadResponse[];
}

export interface RadarLeadResponse {
  responseId: string;
  draftType: 'rfp_outline' | 'cold_email' | 'forum_reply';
  draftStatus: 'pending_review' | 'approved' | 'rejected' | 'sent';
  body: string;
  generatedAt: string;
  sentAt?: string | null;
  reviewerNotes?: string | null;
}

export interface RadarSource {
  sourceCode: string;
  displayName: string;
  family: SourceFamily;
  legalRisk: LegalRisk;
  active: boolean;
  rateLimitPerMinute: number;
  termsReviewedDate?: string | null;
  auditDocumentPath?: string | null;
  lastSuccessfulRunAt?: string | null;
  itemsLast24h: number;
  errorRateLast24h: number;
  aiCostLast24h: number;
}

export interface RadarReviewItem {
  leadId: string;
  artifactId: string;
  title: string;
  category: LeadCategory;
  confidence: number;
  rationale: string;
  capturedAt: string;
}

export interface RadarDashboardSummary {
  hotLeads: number;
  warmLeads: number;
  coldLeads: number;
  sourcesActive: number;
  sourcesTotal: number;
  artifactsLast24h: number;
  classificationsLast24h: number;
  aiCostLast24h: number;
  recentActivity: {
    leadId: string;
    title: string;
    tier: LeadTier;
    sourceCode: string;
    capturedAt: string;
  }[];
}

export interface RadarLeadListFilters {
  tier?: LeadTier;
  category?: LeadCategory;
  source?: string;
  status?: LeadStatus;
  since?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface RadarLeadPatch {
  status?: LeadStatus;
  notes?: string;
  assignedTo?: string | null;
}

export interface RadarReviewDecision {
  action: 'approve' | 'override' | 'reject';
  overrideCategory?: LeadCategory;
  reason?: string;
}

// ════════════════════════════════════════════════════════════════════════════
// Hook
// ════════════════════════════════════════════════════════════════════════════

export function useRadarApi() {
  const { createApi } = useApiClient();

  const listLeads = useCallback(async (filters: RadarLeadListFilters = {}): Promise<RadarLeadSummary[]> => {
    const api = await createApi();
    const { data } = await api.get<RadarLeadSummary[]>('/api/radar/leads', { params: filters });
    return data;
  }, [createApi]);

  const getLead = useCallback(async (leadId: string): Promise<RadarLeadDetail> => {
    const api = await createApi();
    const { data } = await api.get<RadarLeadDetail>(`/api/radar/leads/${leadId}`);
    return data;
  }, [createApi]);

  const patchLead = useCallback(async (leadId: string, patch: RadarLeadPatch): Promise<RadarLeadDetail> => {
    const api = await createApi();
    const { data } = await api.patch<RadarLeadDetail>(`/api/radar/leads/${leadId}`, patch);
    return data;
  }, [createApi]);

  const draftResponse = useCallback(async (
    leadId: string,
    draftType: RadarLeadResponse['draftType'],
    notes?: string,
  ): Promise<RadarLeadResponse> => {
    const api = await createApi();
    const { data } = await api.post<RadarLeadResponse>(
      `/api/radar/leads/${leadId}/responses`,
      { draftType, notes },
    );
    return data;
  }, [createApi]);

  const approveResponse = useCallback(async (leadId: string, responseId: string): Promise<RadarLeadResponse> => {
    const api = await createApi();
    const { data } = await api.post<RadarLeadResponse>(
      `/api/radar/leads/${leadId}/responses/${responseId}/approve`,
      {},
    );
    return data;
  }, [createApi]);

  const rejectResponse = useCallback(async (
    leadId: string,
    responseId: string,
    reason?: string,
  ): Promise<RadarLeadResponse> => {
    const api = await createApi();
    const { data } = await api.post<RadarLeadResponse>(
      `/api/radar/leads/${leadId}/responses/${responseId}/reject`,
      { reason },
    );
    return data;
  }, [createApi]);

  const listSources = useCallback(async (): Promise<RadarSource[]> => {
    const api = await createApi();
    const { data } = await api.get<RadarSource[]>('/api/radar/sources');
    return data;
  }, [createApi]);

  const toggleSource = useCallback(async (sourceCode: string, active: boolean): Promise<RadarSource> => {
    const api = await createApi();
    const { data } = await api.patch<RadarSource>(`/api/radar/sources/${sourceCode}`, { active });
    return data;
  }, [createApi]);

  const listReviewQueue = useCallback(async (): Promise<RadarReviewItem[]> => {
    const api = await createApi();
    const { data } = await api.get<RadarReviewItem[]>('/api/radar/review-queue');
    return data;
  }, [createApi]);

  const decideReview = useCallback(async (leadId: string, decision: RadarReviewDecision): Promise<void> => {
    const api = await createApi();
    await api.post(`/api/radar/review-queue/${leadId}`, decision);
  }, [createApi]);

  const getDashboard = useCallback(async (): Promise<RadarDashboardSummary> => {
    const api = await createApi();
    const { data } = await api.get<RadarDashboardSummary>('/api/radar/dashboard');
    return data;
  }, [createApi]);

  // Memoize the returned object — without this the consumer sees a fresh
  // object literal every render, which destabilizes any hook that has
  // `api` in its dep array and produces a request-per-render loop.
  return useMemo(() => ({
    listLeads,
    getLead,
    patchLead,
    draftResponse,
    approveResponse,
    rejectResponse,
    listSources,
    toggleSource,
    listReviewQueue,
    decideReview,
    getDashboard,
  }), [
    listLeads,
    getLead,
    patchLead,
    draftResponse,
    approveResponse,
    rejectResponse,
    listSources,
    toggleSource,
    listReviewQueue,
    decideReview,
    getDashboard,
  ]);
}
